import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Testbed } from '../../../model/testbed';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { EMPTY, Observable, of } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { switchMap, map, tap, catchError } from 'rxjs/operators';
import { TestbedsService } from '../../../services/testbeds.service';
import { StorageFile } from '../../../model/storageFile';
import { FileSaverService } from 'ngx-filesaver';
import { Constants } from "../../../utils/constants";
import { AuthService } from "../../../services/auth.service";
import { Contract } from "../../../model/contract";
import { DialogsService } from '../../../services/dialogs.service';
import { SnackService } from '../../../services/snack.service';

@Component({
  selector: 'aw-list-testbed-files',
  templateUrl: './list-testbed-files.component.html',
  styleUrls: ['./list-testbed-files.component.scss']
})
export class ListTestbedFilesComponent implements OnInit {

  public displayedColumns = [
    'name',
    'lastModified',
    'size',
    'action'
  ];
  @ViewChild('paginatorTestbeds') paginator!: MatPaginator;
  @Output() filesReady = new EventEmitter<StorageFile[]>();
  @Output() requestClose = new EventEmitter();
  loading = false;
  dataSource!: MatTableDataSource<StorageFile>;
  disabledItemsIds: string[] = [];
  downloadingProgress: { [id: string]: number } = {};
  roles = Constants.roles;
  contractStatuses = Constants.contractStatuses;
  loggedUser?: any;
  currentContract?: Contract;
  private allFiles$!: Observable<StorageFile[]>;
  private testbed$!: Observable<Testbed>;

  constructor(
    private authService: AuthService,
    private testbedService: TestbedsService,
    private fileSaverService: FileSaverService,
    private dialogsService: DialogsService,
    private snackService: SnackService,
  ) {

    this.testbed$ =
      of(true)
        .pipe(
          switchMap(() => {
            if (this.loggedUser.role === this.roles.customer)
              return this.testbedService.getTestbedById(this._testbed?.id);
            return this.testbedService.getTestbedById(this._testbed?.id);
          }),
          tap(x => this._testbed = x)
        );


    this.allFiles$ =
      of(true)
        .pipe(
          tap(() => {
            this.loading = true;
          }),
          switchMap(() => (this._testbed ?
            this.testbedService
              .getTestbedFiles(this._testbed) :
            of([])
          )),
          catchError(err => {
            this.snackService.error("You are not authorized to access this storage.")
            this.requestClose.emit();
            return EMPTY;
          }),
          tap(x => {
            this.dataSource = new MatTableDataSource<StorageFile>(x);
            this.filesReady.emit(x);
            this.dataSource.paginator = this.paginator;
            this.loading = false;
          })
        );
  }

  @Input() set updateListOn(value: Observable<any> | undefined) {
    if (value)
      value.subscribe(() => {
        this.allFiles$.subscribe();
        this.testbed$.subscribe();
      });
  }

  private _testbed: Testbed = new Testbed();

  @Input() set testbed(value: Testbed) {
    if (value) {
      this._testbed = value;
    }
  }

  ngOnInit(): void {
    this.loggedUser = this.authService.loggedUser();
  }


  ngAfterViewInit(): void {
    this.allFiles$.subscribe();
  }

  canIDeleteFile(storageFile: StorageFile): boolean {
    // FIXME: This is not the correct check; only owners of testbed or admins can delete files. Anyway, this check
    // done in backend, so bug is only visual and some customer that are not able to delete will receive an error message
    // trying to delete files.
    return this.loggedUser.role === this.roles.admin || this.loggedUser.role === this.roles.customer;
  }
  applyFilters(event: string): void {
    this.dataSource.filter = event.trim().toLowerCase();
  }

  doSortFiles(event: Sort): void {
    this.dataSource.paginator?.firstPage();
    this.dataSource.data = this.dataSource.data.sort((a, b) => {

      const index = event.active;
      if (event.direction === 'asc') {
        return ('' + (b as any)[index]).localeCompare('' + (a as any)[index]);
      } else {
        return ('' + (a as any)[index]).localeCompare('' + (b as any)[index]);
      }
    }
    );

  }

  download(item: StorageFile): void {
    this.disabledItemsIds.push(item.name);
    this.snackService.info(`Download will start automatically in few seconds, please wait...`);

    const $obs = this.testbedService.getSignedUrlForDownload(item.name, this._testbed.id)
      .pipe(
        switchMap(response => {
          return this.testbedService.downloadFile(response.data)
            .pipe(
              map(file => {
                this.snackService.dismiss();
                this.fileSaverService
                  .save(file, item.name);
                this.snackService.success(`Download completed.`);
                this.disabledItemsIds.splice(this.disabledItemsIds.indexOf(item.name), 1);
              }),
              catchError((err) => {
                this.snackService.error(err);
                return err;
              }));
        }),
        catchError(err => {
          this.snackService.error(`${err.message}, operation aborted...`);
          return err;
        }));
    $obs.subscribe();
  }

  deleteFile(storageFile: StorageFile): void {

    this.dialogsService
      .confirm('Delete file', `Are you sure you want to delete the file "${storageFile.name}"?`)
      .pipe(
        switchMap((value) => {
          this.disabledItemsIds.push(storageFile.name);
          return value ?
            this.testbedService.deleteFile(this._testbed, storageFile) :
            EMPTY;
        }),
        switchMap(() => this.allFiles$))
      .subscribe(() => {
        this.snackService.success(`File ${storageFile.name} deleted!`);
        this.disabledItemsIds.splice(this.disabledItemsIds.indexOf(storageFile.name), 1);
      }, () => {
        this.snackService.error(`Cannot delete file ${storageFile.name}`);
        this.disabledItemsIds.splice(this.disabledItemsIds.indexOf(storageFile.name), 1);
      },
        () => {
          this.disabledItemsIds.splice(this.disabledItemsIds.indexOf(storageFile.name), 1);
        });
  }
}
